import type { TableFilterState, TableSortingState } from '@ibsys/kit';
import { useCallback, useMemo } from 'react';
import { CONVERSATION_IDS_FILTER_ID } from '@/entities/conversations';
import type { TablePreset } from '@/entities/settings';
import { DEFAULT_DIALOG_SORTING } from './constants';

/** Параметры действий над слоем вида списка (сброс/снятие выборки или пресета) */
type Params = {
  /** Активный пресет таблицы или `null` */
  activePreset: TablePreset | null;
  /** Активный пресет донастроен относительно снимка */
  isPresetDirty: boolean;
  /** Повторно применяет снимок пресета (откат донастройки) */
  applyPreset: (presetId: string) => void;
  /** Входит в вид: заменяет фильтры/сортировку, не трогая колонки */
  enterView: (view: { filters: TableFilterState; sorting: TableSortingState }) => void;
  /** Убирает внешнюю выборку из отбора таблицы, возвращая вид, действовавший до перехода по ней */
  exitSelection: (selectionFilterId: string) => void;
  /** Действующие фильтры выборки (с наложенным слоем перехода) */
  filters: TableFilterState;
  /** Личная сортировка таблицы (рабочая запись) */
  sorting: TableSortingState | undefined;
  /** Ключ действующей выборки среди фильтров; `null` — выборки нет */
  activeSelectionKey: string | null;
  /** Записывает фильтры таблицы */
  onFiltersChange: (filters: TableFilterState) => void;
  /** Сбрасывает основной поиск */
  onClear: () => void;
  /** Сбрасывает поиск по содержимому */
  onResetContentSearch: () => void;
};

/** Доступность и обработчики сброса фильтров и снятия выборки */
type Result = {
  /** Есть что сбрасывать */
  canReset: boolean;
  /** Можно записать донастройку в активный пресет */
  canSaveActivePreset: boolean;
  /** Можно снять несохранённую выборку перехода */
  canDeselectSelection: boolean;
  onResetFilters: () => void;
  onDeselectSelection: () => void;
};

/**
 * Действия над слоем вида списка диалогов. Выборка/пресет — отдельный слой поверх личных настроек:
 * сброс фильтров и снятие слоя — разные действия. Сброс откатывает донастройку к базе слоя
 * (пресету/выборке), снятие выходит из слоя: пресет — к отложенному личному виду, выборка —
 * к виду, действовавшему до перехода по ней (виду пресета или личному), убирая выборку из отбора.
 */
export const useFilterLayerActions = ({
  activePreset,
  isPresetDirty,
  applyPreset,
  enterView,
  exitSelection,
  filters,
  sorting,
  activeSelectionKey,
  onFiltersChange,
  onClear,
  onResetContentSearch,
}: Params): Result => {
  const selectionFilters = useMemo(
    () => filters.filter((filter) => filter.id === CONVERSATION_IDS_FILTER_ID),
    [filters],
  );
  const userFilters = useMemo(() => filters.filter((filter) => filter.id !== CONVERSATION_IDS_FILTER_ID), [filters]);

  const hasSelectionLayer = activeSelectionKey !== null;
  const isDefaultSorting =
    (sorting?.length ?? 0) === 1 &&
    sorting?.[0]?.id === DEFAULT_DIALOG_SORTING[0].id &&
    sorting?.[0]?.desc === DEFAULT_DIALOG_SORTING[0].desc;

  // Есть ли что сбрасывать: для пресета — донастройка (грязный слой); для выборки — добавленные
  // поверх фильтры/сортировка; без слоя — любые обычные фильтры.
  const canReset = activePreset
    ? isPresetDirty
    : hasSelectionLayer
      ? userFilters.length > 0 || !isDefaultSorting
      : filters.length > 0;

  // Записать донастройку в активный пресет можно, только когда пресет активен, донастроен и
  // пользователь вправе его править: без активного пресета сохранять не во что, без изменений —
  // нечего, а чужой системный пресет донастройка не меняет — ему предлагается личная копия.
  const canSaveActivePreset = !!activePreset && activePreset.isEditable && isPresetDirty;

  // Снять несохранённую выборку перехода можно отдельным контролом рядом со сбросом; у сохранённого
  // как пресет слоя снятие живёт в строке активного пресета.
  const canDeselectSelection = hasSelectionLayer && !activePreset;

  const onResetFilters = useCallback(() => {
    if (activePreset) {
      // Откат донастройки к пресету: повторно применяем его снимок, пресет остаётся активным
      applyPreset(activePreset.id);
      return;
    }
    if (hasSelectionLayer) {
      // Откат донастройки к выборке: оставляем только саму выборку и сортировку по умолчанию,
      // не снимая слой и не трогая колонки
      enterView({ filters: selectionFilters, sorting: DEFAULT_DIALOG_SORTING });
      return;
    }
    // Нет слоя — обычный сброс фильтров и поиска
    onFiltersChange([]);
    onClear();
    // Поиск по содержимому (в т.ч. выбранные авторы комментариев) сбрасывается наравне
    // с основным поиском, чтобы следующий поиск не оставался сужен ранее выбранным автором.
    onResetContentSearch();
  }, [
    activePreset,
    applyPreset,
    hasSelectionLayer,
    enterView,
    selectionFilters,
    onFiltersChange,
    onClear,
    onResetContentSearch,
  ]);

  // Снятие выборки убирает её из отбора в любом состоянии: поля у выборки нет, и другого выхода
  // из неё у пользователя не остаётся. Куда вернётся таблица (вид пресета, отложенный личный вид
  // или текущий отбор без выборки) — решает состояние слоя.
  const onDeselectSelection = useCallback(() => {
    exitSelection(CONVERSATION_IDS_FILTER_ID);
  }, [exitSelection]);

  return { canReset, canSaveActivePreset, canDeselectSelection, onResetFilters, onDeselectSelection };
};
